import { lazy } from 'react';
// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import BusinessList from 'views/business';
import InviteBusinessForm from 'views/business/forms/invite-business';
import EditPage from 'views/pageManagement/forms/editPage';
import PromoCodeList from 'views/promoCode';
import PromoCodeAddForm from 'views/promoCode/forms/add-promo-code';
import BrandedAppPromoCodeList from 'views/brandedAppPromoCode';
import BrandedAppPromoCodeForm from 'views/brandedAppPromoCode/forms/add-branded-app-promo-code';

import ERoutes from './ERoutes';

// dashboard routing
const Dashboard = Loadable(lazy(() => import('views/dashboard')));
const Calendar = Loadable(lazy(() => import('views/calendar')));
const PromoCodes = Loadable(lazy(() => import('views/promoCode/combined')));
const DashboardDefault = Loadable(lazy(() => import('views/dashboard/default')));

// page management routing
const PageList = Loadable(lazy(() => import('views/pageManagement')));
const AdvancedPageList = Loadable(lazy(() => import('views/pageManagement/advancedPage')));
const AddPage = Loadable(lazy(() => import('views/pageManagement/forms/addPage')));

// integration card routing
const IntegrationCardList = Loadable(lazy(() => import('views/integrationCard')));
const IntegrationCardForm = Loadable(lazy(() => import('views/integrationCard/forms/integration-card-form')));

// menu routing
const TaxonomyList = Loadable(lazy(() => import('views/taxonomy/taxonomyList')));
const ViewTaxonomy = Loadable(lazy(() => import('views/taxonomy/taxonsList')));
const AddTaxon = Loadable(lazy(() => import('views/taxonomy/forms/addTaxonForm')));
const EditTaxon = Loadable(lazy(() => import('views/taxonomy/forms/editTaxonForm')));

// User managemenet routing
const AdminList = Loadable(lazy(() => import('views/userManagement/admins')));
const AdminAdd = Loadable(lazy(() => import('views/userManagement/admins/forms/add-admin')));
const TeamMemberProfile = Loadable(lazy(() => import('views/userManagement/admins/profile')));
const AdminProfile = Loadable(lazy(() => import('views/profile')));
const AdminAccountProfile = Loadable(lazy(() => import('views/profile/user-profile')));
const AdminChangePassword = Loadable(lazy(() => import('views/profile/change-password')));
const UserList = Loadable(lazy(() => import('views/userManagement/users')));
const UserProfile = Loadable(lazy(() => import('views/userManagement/users/profile')));

/* email template */
const EmailTemplateList = Loadable(lazy(() => import('views/emailTemplate')));
const AddEmailTemplate = Loadable(lazy(() => import('views/emailTemplate/forms/addTemplate')));
const EditEmailTemplate = Loadable(lazy(() => import('views/emailTemplate/forms/editTemplate')));

// Mock component
const MockComponent = Loadable(lazy(() => import('views/mock')));

// Error routing
// const ErrorPage = Loadable(lazy(() => import('views/pages/maintenance/error')));

/* settings */
const SettingsList = Loadable(lazy(() => import('views/settings/settingsList')));

/* crm */
const CrmBoard = Loadable(lazy(() => import('views/crm/board')));
const CrmJourney = Loadable(lazy(() => import('views/crm/journey')));
const CrmJourneyEnrollments = Loadable(lazy(() => import('views/crm/journey/EnrollmentList')));
const CrmAccountDetail = Loadable(lazy(() => import('views/crm/account')));
const CrmContacts = Loadable(lazy(() => import('views/crm/contacts')));
const CrmContactsImport = Loadable(lazy(() => import('views/crm/contacts-import')));
const CrmContactDetail = Loadable(lazy(() => import('views/crm/contact')));
const CrmTasks = Loadable(lazy(() => import('views/crm/tasks')));
const CrmAutomations = Loadable(lazy(() => import('views/crm/automations')));
const CrmReporting = Loadable(lazy(() => import('views/crm/reporting')));
const CrmForms = Loadable(lazy(() => import('views/crm/forms')));
const CrmEmailTemplates = Loadable(lazy(() => import('views/crm/emailTemplates')));
const CrmContractTemplates = Loadable(lazy(() => import('views/crm/contractTemplates')));
const CrmAudiences = Loadable(lazy(() => import('views/crm/audiences')));
const CrmCampaigns = Loadable(lazy(() => import('views/crm/campaigns')));
const CrmImport = Loadable(lazy(() => import('views/crm/import')));
const CrmSettings = Loadable(lazy(() => import('views/crm/settings')));
const CrmCalendarSettings = Loadable(lazy(() => import('views/crm/calendarSettings')));
const CrmMeetings = Loadable(lazy(() => import('views/crm/meetings')));

/* communications */
const ImageAssets = Loadable(lazy(() => import('views/communications/imageAssets')));

/* system */
const QueueMonitor = Loadable(lazy(() => import('views/system/queue-monitor')));
const QueueMonitorDetail = Loadable(lazy(() => import('views/system/queue-monitor/detail')));
const FeatureFlags = Loadable(lazy(() => import('views/system/feature-flags')));
const EmailDeliverability = Loadable(lazy(() => import('views/system/email-deliverability')));

/* business deletion requests */
const BusinessDeletionRequests = Loadable(lazy(() => import('views/businessDeletionRequests')));
const CrmDuplicates = Loadable(lazy(() => import('views/crmDuplicates')));

/* branded mobile app requests */
const BrandedAppRequests = Loadable(lazy(() => import('views/brandedAppRequests')));
const BrandedAppRequestDetail = Loadable(lazy(() => import('views/brandedAppRequests/detail')));

/* reporting */
const ReportingBusinesses = Loadable(lazy(() => import('views/reporting/businesses')));
const ReportingSalesReport = Loadable(lazy(() => import('views/reporting/salesReport')));
const ReportingUnsubscribed = Loadable(lazy(() => import('views/reporting/unsubscribed')));
const ReportingCommunicationReports = Loadable(lazy(() => import('views/reporting/communicationReports')));

/* onboarding dashboard */
const OnboardingDashboard = Loadable(lazy(() => import('views/onboardingDashboard')));

/* onboarding templates editor */
const OnboardingTemplates = Loadable(lazy(() => import('views/onboardingTemplates')));

/* onboarding questionnaire editor */
const OnboardingQuestionnaire = Loadable(lazy(() => import('views/onboardingQuestionnaire')));

/* training manual template editor */
const TrainingManualTemplate = Loadable(lazy(() => import('views/trainingManualTemplate')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard>
            <MainLayout />
        </AuthGuard>
    ),
    children: [
        {
            path: 'dashboard',
            element: <Dashboard />
        },
        {
            path: 'default',
            element: <DashboardDefault />
        },

        {
            path: 'taxonomy/list',
            element: <TaxonomyList />
        },
        {
            path: 'taxonomy/view/:taxonomyId',
            element: <ViewTaxonomy />
        },
        {
            path: 'taxon/add/:taxonomyId',
            element: <AddTaxon />
        },
        {
            path: 'taxon/edit/:taxonomyId/:id',
            element: <EditTaxon />
        },

        {
            path: ERoutes.integrationCardList,
            element: <IntegrationCardList />,
            allowedRoles: ['superAdmin']
        },
        {
            path: ERoutes.integrationCardAdd,
            element: <IntegrationCardForm />,
            allowedRoles: ['superAdmin']
        },
        {
            path: ERoutes.integrationCardEdit,
            element: <IntegrationCardForm />,
            allowedRoles: ['superAdmin']
        },
        {
            path: 'page-management/add',
            element: <AddPage />
        },
        {
            path: ERoutes.pageEdit,
            element: <EditPage />
        },
        {
            path: ERoutes.pageList,
            element: <PageList />
        },
        {
            path: 'page-management/add/advance',
            element: <AdvancedPageList />
        },
        {
            path: 'user/list',
            element: <UserList />
        },
        {
            path: 'user/profile/:id',
            element: <UserProfile />
        },

        {
            path: 'admin/list',
            element: <AdminList />
        },
        {
            path: 'admin/add',
            element: <AdminAdd />
        },
        {
            path: 'admin/team-member/:id',
            element: <TeamMemberProfile />,
            allowedRoles: ['superAdmin']
        },
        {
            path: 'admin/profile',
            element: <AdminProfile />
        },
        {
            path: 'admin/account-profile',
            element: <AdminAccountProfile />
        },
        {
            path: 'admin/change-password',
            element: <AdminChangePassword />
        },

        {
            path: 'email-template/list',
            element: <EmailTemplateList />
        },
        {
            path: 'email-template/add',
            element: <AddEmailTemplate />
        },
        {
            path: 'email-template/edit/:id',
            element: <EditEmailTemplate />
        },

        {
            path: 'mock',
            element: <MockComponent />
        },
        {
            path: 'settings/edit',
            element: <SettingsList />
        },
        {
            path: ERoutes.calendar,
            element: <Calendar />
        },
        {
            path: ERoutes.promoCodes,
            element: <PromoCodes />
        },
        {
            path: ERoutes.promoCodeList,
            element: <PromoCodeList />
        },
        {
            path: ERoutes.promoCodeAdd,
            element: <PromoCodeAddForm />
        },
        {
            path: ERoutes.promoCodeEdit,
            element: <PromoCodeAddForm />
        },
        {
            path: ERoutes.brandedAppPromoCodeList,
            element: <BrandedAppPromoCodeList />
        },
        {
            path: ERoutes.brandedAppPromoCodeAdd,
            element: <BrandedAppPromoCodeForm />
        },
        {
            path: ERoutes.brandedAppPromoCodeEdit,
            element: <BrandedAppPromoCodeForm />
        },
        {
            path: ERoutes.businessList,
            element: <BusinessList />
        },
        {
            path: '/businesses/invite',
            element: <InviteBusinessForm />
        },
        {
            path: ERoutes.crmBoard,
            element: <CrmBoard />
        },
        {
            path: ERoutes.crmJourney,
            element: <CrmJourney />
        },
        {
            path: ERoutes.crmJourneyEnrollments,
            element: <CrmJourneyEnrollments />
        },
        {
            path: ERoutes.crmAccountDetail,
            element: <CrmAccountDetail />
        },
        {
            path: ERoutes.crmContacts,
            element: <CrmContacts />
        },
        {
            path: ERoutes.crmContactsImport,
            element: <CrmContactsImport />
        },
        {
            path: ERoutes.crmContactDetail,
            element: <CrmContactDetail />
        },
        {
            path: ERoutes.crmTasks,
            element: <CrmTasks />
        },
        {
            path: ERoutes.crmAutomations,
            element: <CrmAutomations />
        },
        {
            path: ERoutes.crmReporting,
            element: <CrmReporting />
        },
        {
            path: ERoutes.crmForms,
            element: <CrmForms />
        },
        {
            path: ERoutes.crmEmailTemplates,
            element: <CrmEmailTemplates />
        },
        {
            path: ERoutes.crmContractTemplates,
            element: <CrmContractTemplates />,
            // CONTENT_ROLES — mirrors the resolver access and the Content
            // Management menu group gating (superAdmin + customerService).
            allowedRoles: ['superAdmin', 'customerService']
        },
        {
            path: ERoutes.imageAssets,
            element: <ImageAssets />
        },
        {
            path: ERoutes.crmAudiences,
            element: <CrmAudiences />
        },
        {
            path: ERoutes.crmCampaigns,
            element: <CrmCampaigns />
        },
        {
            path: ERoutes.crmImport,
            element: <CrmImport />
        },
        {
            path: ERoutes.crmSettings,
            element: <CrmSettings />
        },
        {
            path: ERoutes.crmCalendarSettings,
            element: <CrmCalendarSettings />
        },
        {
            path: ERoutes.crmMeetings,
            element: <CrmMeetings />
        },
        {
            path: 'system/queue-monitor',
            element: <QueueMonitor />,
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: 'system/queue-monitor/:queueName',
            element: <QueueMonitorDetail />,
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: ERoutes.featureFlags,
            element: <FeatureFlags />,
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: ERoutes.emailDeliverability,
            element: <EmailDeliverability />,
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: 'businesses/deletion-requests',
            element: <BusinessDeletionRequests />,
            // Matches the nav item and deleteBusiness's own @Auth — the route
            // was previously reachable by hand-typing the URL as any admin.
            allowedRoles: ['superAdmin']
        },
        {
            path: 'businesses/branded-app-requests',
            element: <BrandedAppRequests />,
            // Matches the nav item and the resolver's SYSTEM_ROLES @Auth.
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: 'businesses/branded-app-requests/:businessId',
            element: <BrandedAppRequestDetail />,
            // Same SYSTEM_ROLES as the list and getBrandedAppDetail's @Auth.
            allowedRoles: ['superAdmin', 'developer']
        },
        {
            path: 'businesses/duplicates',
            element: <CrmDuplicates />
        },
        {
            path: 'businesses/onboarding',
            element: <OnboardingDashboard />
        },
        {
            path: 'reporting/businesses',
            element: <ReportingBusinesses />
        },
        {
            // POGO-13862 feedback: the Pogozo sales report (demo/training →
            // free-trial → paid funnel over a rolling window).
            path: 'reporting/sales-report',
            element: <ReportingSalesReport />
        },
        {
            // POGO-13862 Phase 3 (F2): the dedicated Unsubscribed list with
            // cancel date + reason columns the generic business list can't serve.
            path: 'reporting/businesses/unsubscribed',
            element: <ReportingUnsubscribed />
        },
        {
            // POGO-13862 Phase 5: the Communication reports TabSection page
            // (Marketing reports + Bounce list), same query-string pattern as
            // /reports.
            path: 'reporting/communication-reports',
            element: <ReportingCommunicationReports />
        },
        {
            // POGO-13862 (A2): the Reporting group's Onboarding report alias —
            // same view, but under /reporting so its SubNav resolves here.
            path: 'reporting/onboarding',
            element: <OnboardingDashboard />
        },
        {
            path: 'settings/onboarding-templates',
            element: <OnboardingTemplates />
        },
        {
            path: 'settings/onboarding-questionnaire',
            element: <OnboardingQuestionnaire />
        },
        {
            path: 'settings/training-manual',
            element: <TrainingManualTemplate />
        }
    ]
};

export default MainRoutes;
